import { BACKEND_URL } from "@/lib/backendUrl";
import { useState } from "react";
import { FileText, Loader2, Paperclip, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const API_BASE = `${BACKEND_URL}/api`;

export const MinuteRecord = ({ item, onDone }) => {
  const { user } = useAuth();
  const minute = item.minute;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({
    meeting_date: minute?.meeting_date || new Date().toISOString().slice(0, 10),
    meeting_type: minute?.meeting_type || "board",
    attendees: (minute?.attendees || []).join(", "),
    resolution: minute?.resolution || "",
    recusal_note: minute?.recusal_note || "",
    link_to_register: true,
  });
  const maySecretary = ["board_secretary", "org_admin", "platform_admin"].includes(user?.role);

  const save = async () => {
    setBusy("save");
    try {
      await api.post(`/conflicts/clearances/${item.request_ref}/minute`, {
        ...form,
        attendees: form.attendees.split(",").map((a) => a.trim()).filter(Boolean),
      });
      toast.success("Minute recorded against the decision");
      setOpen(false);
      await onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/conflicts/clearances/${item.request_ref}/minute/document`, body,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signed minutes attached");
      await onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (item.status !== "approved" && item.status !== "declined") return null;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3"
         data-testid={`minute-${item.request_ref}`}>
      {minute ? (
        <div className="space-y-1.5 text-[11.5px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="kicker">Board minute</span>
            <span className="text-foreground">
              {minute.meeting_type} meeting · {shortDate(minute.meeting_date)} ·
              {" "}{(minute.attendees || []).length} present
            </span>
            {minute.document && (
              <a href={`${API_BASE}/conflicts/clearances/${item.request_ref}/minute/document`}
                 target="_blank" rel="noreferrer"
                 data-testid={`minute-doc-${item.request_ref}`}
                 className="inline-flex items-center gap-1 text-primary hover:underline">
                <Paperclip className="h-3 w-3" /> {minute.document.filename}
              </a>
            )}
          </div>
          <p className="text-foreground/80">“{minute.resolution}”</p>
          <p className="text-muted-foreground">Recusal: {minute.recusal_note}</p>
          <p className="text-[10.5px] text-muted-foreground">
            Minuted by {minute.minuted_by}
            {(minute.attendees || []).length ? ` · present: ${minute.attendees.join(", ")}` : ""}
          </p>
          {maySecretary && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button size="sm" variant="outline" data-testid={`minute-edit-${item.request_ref}`}
                      onClick={() => setOpen(true)}
                      className="h-7 rounded-full border-border text-[11px]">
                Edit the minute
              </Button>
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-border px-3 py-1 text-[11px] text-muted-foreground hover:text-foreground">
                <Upload className="h-3 w-3" />
                {busy === "upload" ? "Uploading…" : "Replace signed PDF"}
                <input type="file" accept="application/pdf" className="hidden"
                       data-testid={`minute-upload-${item.request_ref}`}
                       onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            </div>
          )}
        </div>
      ) : maySecretary ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11.5px] text-muted-foreground">
            No board minute attached to this decision yet.
          </span>
          <Button size="sm" variant="outline" data-testid={`minute-add-${item.request_ref}`}
                  onClick={() => setOpen(true)}
                  className="h-7 rounded-full border-border text-[11px]">
            <FileText className="mr-1 h-3 w-3" /> Minute this decision
          </Button>
        </div>
      ) : (
        <span className="text-[11.5px] text-muted-foreground">
          Awaiting the Board Secretary's minute.
        </span>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-border bg-card">
          <DialogHeader>
            <DialogTitle>Minute {item.request_ref}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {item.subject} · decision {item.status} with {item.progress.approvals} for and{" "}
            {item.progress.declines} against. The recusal is pre-filled from the register and the
            minute is filed in the meetings register too.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <F label="Meeting date">
              <Input type="date" data-testid="minute-date-input" className="bg-muted/40"
                     value={form.meeting_date}
                     onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} />
            </F>
            <F label="Meeting type">
              <Select value={form.meeting_type}
                      onValueChange={(v) => setForm({ ...form, meeting_type: v })}>
                <SelectTrigger data-testid="minute-type-select" className="bg-muted/40">
                  <SelectValue /></SelectTrigger>
                <SelectContent>
                  {["board", "finance_committee", "audit_committee", "emergency"].map((t) => (
                    <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>))}
                </SelectContent>
              </Select>
            </F>
            <div className="col-span-2"><F label="Attendees (comma separated)">
              <Input data-testid="minute-attendees-input" className="bg-muted/40"
                     value={form.attendees}
                     onChange={(e) => setForm({ ...form, attendees: e.target.value })}
                     placeholder="Ravi Menon, Nikhil Chandra, Dr Fatima Sheikh" />
            </F></div>
            <div className="col-span-2"><F label="Resolution as minuted">
              <Textarea data-testid="minute-resolution-input" rows={3} className="bg-muted/40"
                        value={form.resolution}
                        onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                        placeholder="RESOLVED that the third tranche be released, the declared interest having been noted." />
            </F></div>
            <div className="col-span-2"><F label="Recusal record">
              <Textarea data-testid="minute-recusal-input" rows={2} className="bg-muted/40"
                        value={form.recusal_note}
                        onChange={(e) => setForm({ ...form, recusal_note: e.target.value })}
                        placeholder="Left blank, the register's own wording is used." />
            </F></div>
          </div>
          <Button data-testid="minute-save-button"
                  disabled={busy === "save" || form.resolution.trim().length < 20}
                  onClick={save}
                  className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "save" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            File the minute
          </Button>
          <label className="inline-flex cursor-pointer items-center justify-center gap-1 rounded-full border border-border px-3 py-2 text-[11.5px] text-muted-foreground hover:text-foreground">
            <Upload className="h-3.5 w-3.5" />
            {busy === "upload" ? "Uploading…" : "Attach the signed minutes as a PDF"}
            <input type="file" accept="application/pdf" className="hidden"
                   data-testid="minute-dialog-upload"
                   onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </DialogContent>
      </Dialog>
    </div>
  );
};
